import { useState, useMemo } from "react";

import { AppStrings } from "../../../../utils/constants/AppStrings";
import CustomInput from "../../../CommonComponents/CustomInput/CustomInput";
import PermissionsSection from "../Permissions/PermissionsSection/PermissionsSection";
import ActionsGrid from "../Permissions/ActionsGrid/ActionsGrid";

import { CheckOutlined, DeleteOutlined } from "@ant-design/icons";
import { Modal, Tooltip } from "antd";
import DeleteRoleModal from "./DeleteRoleModal/DeleteRoleModal";

const DEFAULT_COLORS = [
  { value: "#FFFFFF", label: "White", color: "#FFFFFF" },
  { value: "#3498DB", label: "Blue", color: "#3498DB" },
  { value: "#EC4899", label: "Pink", color: "#EC4899" },
  { value: "#D9D9D9", label: "Gray", color: "#D9D9D9" },
  { value: "#000000", label: "Black", color: "#000000" },
  { value: "#FF5733", label: "Orange", color: "#FF5733" },
  { value: "#FFD700", label: "Gold", color: "#FFD700" },
  { value: "#FFA500", label: "Orange", color: "#FFA500" },
  { value: "#6b7280", label: "Slate", color: "#6b7280" },
  { value: "#22c55e", label: "Green", color: "#22c55e" },
  { value: "#9B59B6", label: "Purple", color: "#9B59B6" },
];

function Role({
  schema,
  role,
  originalColor,
  colorOptions,
  handleDeleteRole,
  onRoleNameChange,
  onRoleColorChange,
  onActionToggle,
  onBulkActionChange,
  onReadOnlyAccessChange,
  onPermissionsChange,
  usedColors = [],
  canCreateEditPAMRole,
}) {
  const [isDeleteRoleModalOpen, setIsDeleteRoleModalOpen] = useState(false);
  
  // Exclude this role's own color so it stays selectable
  const colorToExclude = originalColor || role?.color || "";
  const usedColorsLower = useMemo(() => 
    usedColors
      .filter((c) => (c || "").toLowerCase() !== colorToExclude.toLowerCase())
      .map((c) => (c || "").toLowerCase()), 
    [usedColors, colorToExclude]
  );

  const isColorUsed = (colorValue) => usedColorsLower.includes((colorValue || "").toLowerCase());

  if (!role) return null;

  const handleDeleteRolePressed = () => setIsDeleteRoleModalOpen(true);

  const handleDeleteRoleModalOk = () => {
    handleDeleteRole(role.id ?? role.value);
    setIsDeleteRoleModalOpen(false);
  };

  const handleDeleteRoleModalCancel = () => {
    setIsDeleteRoleModalOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleNameChange = (e) => {
    onRoleNameChange?.(e?.target?.value ?? "");
  };

  const actionsList = schema?.available_actions ?? [];
  const permissionCategories = schema?.available_permissions ?? [];
  const colors = colorOptions?.length ? colorOptions : DEFAULT_COLORS;

  return (
    <div className="staff-management-new-role-container">
      <Modal
        closable={false}
        open={isDeleteRoleModalOpen}
        onOk={handleDeleteRoleModalOk}
        onCancel={handleDeleteRoleModalCancel}
        footer={null}
        width={450}
      >
        <DeleteRoleModal
          role={role}
          handleCancel={handleDeleteRoleModalCancel}
          handleDeleteRole={handleDeleteRoleModalOk}
        />
      </Modal>
      <div className="staff-management-new-role-header">
        <h2 className="staff-management-new-role-header-title">{role.name || AppStrings.ROLE_NAME}</h2>
      </div>
      <div className="staff-management-new-role-body">
        <div className="staff-management-new-role-body-input-container">
          <span className="staff-management-new-role-body-input-label">
            {AppStrings.ROLE_NAME}
          </span>
          <CustomInput
            placeholder={AppStrings.ROLE_NAME_PLACEHOLDER}
            className={`staff-management-new-role-body-input-input ${!canCreateEditPAMRole ? "disabled" : ""}`}
            value={role.name ?? ""}
            onChange={handleNameChange}
            disabled={role.is_system_role || !canCreateEditPAMRole}
          />
        </div>
        <div className="staff-management-new-role-body-color-container">
          <span className="staff-management-new-role-body-color-label">
            {AppStrings.ROLE_COLOR}
          </span>
          <div className="staff-management-new-role-body-color-picker">
            {colors.map((c) => {
              const used = isColorUsed(c.value);
              const isDisabled = role.is_system_role || used;
              return (
                <div
                  key={c.value}
                  className={`staff-management-new-role-body-color-picker-color ${!canCreateEditPAMRole || isDisabled ? "disabled" : ""}`}
                  style={{
                    backgroundColor: c.color,
                    opacity: used ? 0.3 : 1,
                    cursor: isDisabled ? "not-allowed" : "pointer",
                  }}
                  disabled={!canCreateEditPAMRole}
                  title={used ? "This color is already used by another role" : undefined}
                  onClick={() => !isDisabled && canCreateEditPAMRole && onRoleColorChange?.(c.color)}
                  role="button"
                  tabIndex={isDisabled ? -1 : 0}
                  onKeyDown={(e) => e.key === "Enter" && !isDisabled && onRoleColorChange?.(c.color)}
                >
                  {role.color === c.color && (
                    <CheckOutlined style={c.value === "#000000" ? { color: "#FFFFFF" } : {}} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <div className="staff-management-new-role-body-columns">
          <PermissionsSection
            categories={permissionCategories}
            permissions={role.permissions ?? {}}
            onPermissionsChange={onPermissionsChange}
            disabled={role.is_system_role || !canCreateEditPAMRole}
          />
          <ActionsGrid
            actions={actionsList}
            values={role.actions ?? {}}
            onToggle={(key) => onActionToggle?.(key)}
            onBulkChange={onBulkActionChange}
            readOnlyAccess={Boolean(role.read_only_access)}
            onReadOnlyAccessChange={onReadOnlyAccessChange}
            disabled={role.is_system_role || !canCreateEditPAMRole}
          />
        </div>
        {!role.is_system_role && (
          <>
            <hr className="staff-management-new-role-body-separator" />
            <div className="staff-management-new-role-body-buttons-container">
              <Tooltip 
                title={role.in_use ? AppStrings.CANNOT_DELETE_ROLE_IN_USE : ""} 
                placement="top"
                color="var(--COLOR-PRIMARY_BACKGROUND_COLOR)"

              >
                <button
                  type="button"
                  className={`staff-management-new-role-body-buttons-button delete-role-button ${role.in_use ? "disabled" : ""}`}
                  onClick={handleDeleteRolePressed}
                  disabled={role.in_use || !canCreateEditPAMRole}
                >
                  <DeleteOutlined style={{ marginRight: "8px" }} />
                  {AppStrings.DELETE_ROLE}
                </button>
              </Tooltip>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default Role;
